import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";
import {
  getFileSignedURLThunk,
  getFileUploadSignedURLThunk,
  postFinalLicenseRowThunk,
  postFinalLicenseThunk,
  postSaveUploadedFileThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { toast } from "react-toastify";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { fetchApplicationDetails } from "../application-details/redux/services/thunk/DetailsThunk";
import { Download, RemoveRedEye } from "@mui/icons-material";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import dayjs from "dayjs";
import SuccessFinalNocModal from "./SuccessFinalNocModal";

interface UploadedDocument {
  fileName: string;
  originalFileName: string;
  objectKey: string;
  documentKey: string;
  isMandatory: boolean;
}

const RequestFinalLicence = ({
  isRequestFinalLicenceOpen,
  setIsRequestFinalLicenceOpen,
  provisional_license_date,
  applicationId,
  onSuccess,
}: {
  isRequestFinalLicenceOpen: boolean;
  setIsRequestFinalLicenceOpen: (open: boolean) => void;
  applicationId: number;
  provisional_license_date?: string;
  onSuccess: () => void;
}) => {
  const [remarks, setRemarks] = useState("");
  const [uploadedDocuments, setUploadedDocuments] = useState<
    UploadedDocument[]
  >([]);

  const [isConfirmationFLOpen, setIsConfirmationFLOpen] = useState(false);

  // Mandatory documents
  const [complianceReportFile, setComplianceReportFile] = useState<File | null>(
    null,
  );
  const [complianceReportObjectKey, setComplianceReportObjectKey] =
    useState<string>("");
  const [asBuiltDrawingsFile, setAsBuiltDrawingsFile] = useState<File | null>(
    null,
  );
  const [asBuiltDrawingsObjectKey, setAsBuiltDrawingsObjectKey] =
    useState<string>("");

  const handleClose = () => {
    setComplianceReportFile(null);
    setAsBuiltDrawingsFile(null);
    setRemarks("");
    setChecked(false);
    setUploadedDocuments([]);
    setIsRequestFinalLicenceOpen(false);
  };

  // Redux hooks
  const dispatch = useDispatch<AppDispatch>();

  const [checked, setChecked] = React.useState(false);
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleUploadFile = async (file: File, documentKey: string) => {
    try {
      // Check total document limit
      const totalDocs =
        uploadedDocuments.length +
        (complianceReportFile ? 1 : 0) +
        (asBuiltDrawingsFile ? 1 : 0);
      if (totalDocs >= 5) {
        toast.error("Maximum 5 documents allowed");
        return;
      }

      const urlRes = await dispatch(
        genericUploadThunk({ file, moduleType: "row" }),
      );
      if (!urlRes?.payload?.success) {
        const urlResDetails =
          urlRes?.payload?.details || urlRes?.payload?.response?.data?.details;
        toast.error(
          Array.isArray(urlResDetails)
            ? urlResDetails?.[0]
            : typeof urlResDetails === "string"
              ? urlResDetails
              : "Something Went Wrong!",
        );
        return;
      }
      if (!productionCheck) {
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: urlRes.payload.data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();

        if (!uploadSuccess) throw new Error("File upload to S3 failed");
      }

      // Determine if document is mandatory
      const isMandatory =
        documentKey === "compliance_report" ||
        documentKey === "as_built_drawings";

      // Add to uploaded documents
      if (documentKey === "compliance_report") {
        setComplianceReportFile(file);
        setComplianceReportObjectKey(urlRes.payload.data.objectKey || "");
      } else if (documentKey === "as_built_drawings") {
        setAsBuiltDrawingsFile(file);
        setAsBuiltDrawingsObjectKey(urlRes.payload.data.objectKey || "");
      } else {
        // For optional supporting documents
        setUploadedDocuments([
          ...uploadedDocuments,
          {
            fileName: file.name,
            originalFileName: urlRes.payload.data.fileName,
            objectKey: urlRes.payload.data.objectKey,
            documentKey,
            isMandatory: false,
          },
        ]);
      }

      toast.success("File uploaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error uploading file");
    }
  };

  const handleFinalSubmit = async () => {
    // Validate mandatory documents
    if (!complianceReportFile || !asBuiltDrawingsFile) {
      toast.error(
        "Please upload both mandatory documents: Compliance Report and As-built Drawings",
      );
      return;
    }

    // Build documents array
    const documents = [
      {
        fileName: complianceReportFile.name,
        originalFileName: complianceReportFile.name,
        objectKey: complianceReportObjectKey,
        documentKey: "compliance_report",
        isMandatory: true,
      },
      {
        fileName: asBuiltDrawingsFile.name,
        originalFileName: asBuiltDrawingsFile.name,
        objectKey: asBuiltDrawingsObjectKey,
        documentKey: "as_built_drawings",
        isMandatory: true,
      },
      ...uploadedDocuments,
    ];

    const payload = {
      applicationId: applicationId,
      remarks: remarks,
      finalLicenseConsent: checked,
      documents: documents,
    }; 
    // return
    const res = await dispatch(postFinalLicenseRowThunk(payload));
    if (res.meta.requestStatus === "fulfilled") {
      // toast.success("Final NOC request submitted successfully.");
      setIsRequestFinalLicenceOpen(false);
      onSuccess?.();
      setIsConfirmationFLOpen(true);
    } else {
      toast.error("Failed to submit final NOC request.");
    }
  };

  // Handler for compliance report upload
  const handleComplianceReportChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUploadFile(file, "compliance_report");
    }
    e.target.value = "";
  };

  // Handler for as-built drawings upload
  const handleAsBuiltDrawingsChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUploadFile(file, "as_built_drawings");
    }
    e.target.value = "";
  };

  // Handler for optional supporting documents upload
  const handleOptionalDocumentsChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        await handleUploadFile(files[i], "optional_support");
      }
    }
    e.target.value = "";
  };

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: doc_name,
      dispatch,
      getSignedUrlThunk: getFileSignedURLThunk,
    });
  };

  return (
    <>
      <Dialog
        open={isRequestFinalLicenceOpen}
        onClose={() => setIsRequestFinalLicenceOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiDialog-paper": { overflow: "unset" } }}
      >
        <DialogTitle className="dialog-title">
          Final NOC Request
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            className="close-icon"
            sx={{ position: "absolute", right: 20, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {/* MANDATORY DOCUMENTS */}
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, color: "#3a1c73" }}
          >
            Mandatory Documents
          </Typography>

          {/* 1. Compliance Report - MANDATORY */}
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: 14 }}>
            i. The compliance report of the provisional NOC{" "}
            <span style={{ color: "red" }}>*</span>
          </Typography>
          <Box
            onClick={() =>
              document.getElementById("compliance-report-input")?.click()
            }
            sx={{
              border: "2px dashed #D3D6DA",
              borderRadius: "12px",
              backgroundColor: "#F8FAFC",
              textAlign: "center",
              py: 3,
              mb: 2,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#F3F0FF", borderColor: "#3a1c73" },
            }}
          >
            <input
              id="compliance-report-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={handleComplianceReportChange}
            />
            <Typography sx={{ fontWeight: 500, color: "#3a1c73" }}>
              Click to upload document (PDF, JPG, PNG)
            </Typography>
          </Box>
          {complianceReportFile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #D3D6DA",
                borderRadius: 2,
                p: 1,
                mb: 3,
                background: "#F8FAFC",
              }}
            >
              <PictureAsPdfIcon
                sx={{ color: "#D32F2F", fontSize: 38, mr: 1 }}
              />
              <Typography flex={1}>{complianceReportFile.name}</Typography>
              <RemoveRedEye
                sx={{
                  mr: 1,
                  fontSize: 20,
                  color: "rgb(0,0,0,0.54)",
                  cursor: "pointer",
                }}
                onClick={() =>
                  handleViewFile(complianceReportObjectKey?.split("/").pop())
                }
              />
              <IconButton
                size="small"
                onClick={() => {
                  setComplianceReportFile(null);
                  setComplianceReportObjectKey("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          {/* 2. As-built Drawings - MANDATORY */}
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: 14 }}>
            ii. The As-built Drawings <span style={{ color: "red" }}>*</span>
          </Typography>
          <Box
            onClick={() =>
              document.getElementById("as-built-drawings-input")?.click()
            }
            sx={{
              border: "2px dashed #D3D6DA",
              borderRadius: "12px",
              backgroundColor: "#F8FAFC",
              textAlign: "center",
              py: 3,
              mb: 2,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#F3F0FF", borderColor: "#3a1c73" },
            }}
          >
            <input
              id="as-built-drawings-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={handleAsBuiltDrawingsChange}
            />
            <i
              className="fa fa-file-pdf"
              style={{
                fontSize: 28,
                color: "#D32F2F",
                marginBottom: 8,
                display: "block",
              }}
            />
            <Typography sx={{ fontWeight: 500, color: "#3a1c73" }}>
              Click to upload document (PDF, JPG, PNG)
            </Typography>
          </Box>
          {asBuiltDrawingsFile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #D3D6DA",
                borderRadius: 2,
                p: 1,
                mb: 3,
                background: "#F8FAFC",
              }}
            >
              <PictureAsPdfIcon
                sx={{ color: "#D32F2F", fontSize: 38, mr: 1 }}
              />
              <Typography flex={1}>{asBuiltDrawingsFile.name}</Typography>
              <RemoveRedEye
                sx={{
                  mr: 1,
                  fontSize: 20,
                  color: "rgb(0,0,0,0.54)",
                  cursor: "pointer",
                }}
                onClick={() =>
                  handleViewFile(asBuiltDrawingsObjectKey?.split("/").pop())
                }
              />
              <IconButton
                size="small"
                onClick={() => {
                  setAsBuiltDrawingsFile(null);
                  setAsBuiltDrawingsObjectKey("");
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          {/* OPTIONAL DOCUMENTS */}
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, color: "#3a1c73", mt: 3 }}
          >
            Optional Supporting Documents
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            Upload upto 3 additional supporting documents:
            <br />
          </Typography>

          {/* Optional Document Upload */}
          {uploadedDocuments.length < 3 && (
            <Box
              onClick={() =>
                document.getElementById("optional-docs-input")?.click()
              }
              sx={{
                border: "2px dashed #D3D6DA",
                borderRadius: "12px",
                backgroundColor: "#F8FAFC",
                textAlign: "center",
                py: 3,
                mb: 2,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#F3F0FF",
                  borderColor: "#3a1c73",
                },
              }}
            >
              <input
                id="optional-docs-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                style={{ display: "none" }}
                onChange={handleOptionalDocumentsChange}
              />
              <i
                className="fa fa-cloud-upload"
                style={{
                  fontSize: 28,
                  color: "#3a1c73",
                  marginBottom: 8,
                  display: "block",
                }}
              />
              <Typography sx={{ fontWeight: 500, color: "#3a1c73" }}>
                Click to upload supporting documents
              </Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>
                PDF, JPG, PNG, DOC, DOCX (Max 3 documents)
              </Typography>
            </Box>
          )}

          {/* Display uploaded optional documents */}
          {uploadedDocuments.length > 0 && (
            <Box mb={3}>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
                Uploaded Supporting Documents:
              </Typography>
              {uploadedDocuments.map((doc, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #D3D6DA",
                    borderRadius: 2,
                    p: 1,
                    mb: 1,
                    background: "#F8FAFC",
                  }}
                >
                  <PictureAsPdfIcon
                    sx={{ color: "#D32F2F", fontSize: 38, mr: 1 }}
                  />
                  <Typography flex={1}>{doc.fileName}</Typography>
                  <RemoveRedEye
                    sx={{
                      mr: 1,
                      fontSize: 20,
                      color: "rgb(0,0,0,0.54)",
                      cursor: "pointer",
                    }}
                    onClick={() => handleViewFile(doc.originalFileName)}
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setUploadedDocuments(
                        uploadedDocuments.filter((_, i) => i !== idx),
                      )
                    }
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Remarks */}
          <Box mb={2}>
            <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
              Remarks (Optional)
            </Typography>
            <TextField
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add your remark here... (Optional)"
              fullWidth
              multiline
              rows={4}
              inputProps={{ maxLength: 500 }}
            />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: "right", mt: 0.5 }}
            >
              {remarks.length}/500
            </Typography>
          </Box>

          {/* Consent Checkbox */}
          <Box>
            <FormControlLabel
              sx={{
                alignItems: "start",
                textAlign: "justify",
                lineHeight: 1.4,
              }}
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleCheckboxChange}
                  sx={{
                    color: "#3a1c73",
                    "&.Mui-checked": {
                      color: "#3a1c73",
                    },
                  }}
                />
              }
              label="I hereby submit the compliance report and the proposal for granting the final NOC and affirm that all information furnished is accurate and duly verified. I acknowledge that no modifications shall be permitted once the application is submitted."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            sx={{
              borderRadius: "30px",
              backgroundColor: "#3A1C73",
              color: "#fff",
              fontWeight: 500,
              fontSize: "1.25rem",
              textTransform: "none",
              "&.Mui-disabled": {
                backgroundColor: "#D3D6DA",
                color: "#fff",
              },
            }}
            disabled={!checked || !complianceReportFile || !asBuiltDrawingsFile}
            onClick={handleFinalSubmit}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
      <SuccessFinalNocModal
        isConfirmationFLOpen={isConfirmationFLOpen}
        setIsConfirmationFLOpen={setIsConfirmationFLOpen}
        setOpenSlider={handleClose}
        onSuccessClose={() => dispatch(fetchApplicationDetails(applicationId))}
      />
    </>
  );
};

export default RequestFinalLicence;
